(() => {
  'use strict';

  function normalise(value) {
    return String(value || '').toLowerCase().replace(/\s+/g, ' ').trim();
  }

  function getVariantText(variant) {
    return normalise([
      variant?.title,
      variant?.sku,
      ...(variant?.options || []),
      variant?.option1,
      variant?.option2,
      variant?.option3
    ].filter(Boolean).join(' '));
  }

  function resetCompatibilityToggle(panel) {
    const button = panel.querySelector('[data-eco-compatibility-toggle]');
    if (!button) return;

    const visibleExtras = Array.from(
      panel.querySelectorAll('.eco-compatible-product--extra')
    ).filter((item) => !item.classList.contains('is-variant-hidden'));

    button.hidden = visibleExtras.length === 0;

    if (button.hidden) {
      panel.querySelectorAll('.eco-compatible-product--extra').forEach((item) => {
        item.classList.add('is-hidden');
      });
      button.setAttribute('aria-expanded', 'false');
      button.textContent = button.dataset.showText;
    }
  }

  function updateCompatibilityPanel(panel, variant) {
    const triggers = String(panel.dataset.triggerValues || '')
      .split('|')
      .map(normalise)
      .filter(Boolean);

    const variantText = getVariantText(variant);
    const triggerMatched = triggers.length === 0 ||
      triggers.some((trigger) => variantText.includes(trigger));

    // Driver cards are variant-specific. Accessories and lamps remain available
    // unless they have been explicitly marked as variant-hidden elsewhere.
    panel.querySelectorAll('[data-eco-recommendation-type="driver"]').forEach((card) => {
      card.classList.toggle('is-variant-hidden', !triggerMatched);
    });

    // Colour-matched compatible variants (for example white head → white track).
    panel.querySelectorAll('[data-eco-variant-match]').forEach((card) => {
      const match = normalise(card.dataset.ecoVariantMatch || '');
      const colourMatched = match === '' || variantText.includes(match);
      card.classList.toggle('is-variant-hidden', !colourMatched);
    });

    // Never leave the heading/notice panel visible when there is no actual
    // recommendation card that applies to the selected variant.
    const hasApplicableRecommendation = Array.from(
      panel.querySelectorAll('[data-eco-recommendation-type]')
    ).some((card) => !card.classList.contains('is-variant-hidden'));

    panel.classList.toggle('is-hidden', !hasApplicableRecommendation);
    resetCompatibilityToggle(panel);
  }

  function initialiseCompatibilityPanels() {
    document.querySelectorAll('[data-eco-compatibility]').forEach((panel) => {
      updateCompatibilityPanel(panel, {
        title: panel.dataset.initialVariant || '',
        sku: '',
        options: []
      });
    });
  }

  function updatePanelsForVariantEvent(event) {
    const variant = event.detail?.variant || {};
    const eventForm = event.target instanceof HTMLFormElement
      ? event.target
      : event.target?.closest?.('form');

    const panels = eventForm
      ? eventForm.querySelectorAll('[data-eco-compatibility]')
      : document.querySelectorAll('[data-eco-compatibility]');

    panels.forEach((panel) => updateCompatibilityPanel(panel, variant));
  }

  document.addEventListener('variant:change', updatePanelsForVariantEvent);
  document.addEventListener('variant:changed', updatePanelsForVariantEvent);

  function getDateInTimeZone(timeZone) {
    try {
      const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).formatToParts(new Date());

      const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
      return new Date(Date.UTC(
        Number(values.year),
        Number(values.month) - 1,
        Number(values.day),
        12
      ));
    } catch (error) {
      const now = new Date();
      return new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), 12));
    }
  }

  function isoDate(date) {
    return [
      date.getUTCFullYear(),
      String(date.getUTCMonth() + 1).padStart(2, '0'),
      String(date.getUTCDate()).padStart(2, '0')
    ].join('-');
  }

  function isBusinessDay(date, excludedDates) {
    const day = date.getUTCDay();
    return day !== 0 && day !== 6 && !excludedDates.has(isoDate(date));
  }

  function addBusinessDays(startDate, numberOfDays, excludedDates) {
    const date = new Date(startDate.getTime());
    let added = 0;

    if (numberOfDays === 0) {
      while (!isBusinessDay(date, excludedDates)) {
        date.setUTCDate(date.getUTCDate() + 1);
      }
      return date;
    }

    while (added < numberOfDays) {
      date.setUTCDate(date.getUTCDate() + 1);
      if (isBusinessDay(date, excludedDates)) {
        added += 1;
      }
    }

    return date;
  }

  function formatDispatchDate(date) {
    return new Intl.DateTimeFormat('en-AU', {
      timeZone: 'UTC',
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    }).format(date);
  }

  function initialiseDispatchPanels() {
    document.querySelectorAll('[data-eco-dispatch]').forEach((panel) => {
      const minDays = Math.max(0, Number(panel.dataset.minDays || 1));
      const maxDays = Math.max(minDays, Number(panel.dataset.maxDays || 3));
      const timeZone = panel.dataset.timeZone || 'Australia/Sydney';
      const excludedDates = new Set(
        String(panel.dataset.excludedDates || '')
          .split(',')
          .map((value) => value.trim())
          .filter((value) => /^\d{4}-\d{2}-\d{2}$/.test(value))
      );

      const today = getDateInTimeZone(timeZone);
      const earliest = addBusinessDays(today, minDays, excludedDates);
      const latest = addBusinessDays(today, maxDays, excludedDates);
      const output = panel.querySelector('[data-eco-dispatch-date]');

      if (output) {
        output.textContent = `${formatDispatchDate(earliest)} - ${formatDispatchDate(latest)}`;
      }
    });
  }



  function initialiseCompatibilityToggles() {
    document.querySelectorAll('[data-eco-compatibility-toggle]').forEach((button) => {
      button.addEventListener('click', () => {
        const panel = button.closest('[data-eco-compatibility]');
        if (!panel) return;

        const extras = panel.querySelectorAll('.eco-compatible-product--extra');
        const isExpanded = button.getAttribute('aria-expanded') === 'true';

        extras.forEach((item) => item.classList.toggle('is-hidden', isExpanded));
        button.setAttribute('aria-expanded', String(!isExpanded));
        button.textContent = isExpanded
          ? button.dataset.showText
          : button.dataset.hideText;
      });
    });
  }


  function isTwoColumnSpecificationTable(table) {
    if (
      table.classList.contains('eco-variant-guide') ||
      table.dataset.ecoTable === 'variant-guide' ||
      table.closest('.eco-variant-guide-wrapper')
    ) {
      return false;
    }

    const rows = Array.from(table.querySelectorAll('tr'));
    const populatedRows = rows.filter((row) =>
      Array.from(row.querySelectorAll(':scope > td, :scope > th'))
        .some((cell) => normalise(cell.textContent) !== '')
    );

    if (populatedRows.length === 0) return false;

    return populatedRows.every((row) => {
      const cells = Array.from(row.querySelectorAll(':scope > td, :scope > th'));
      return cells.length === 2 && cells.every((cell) => Number(cell.getAttribute('colspan') || 1) === 1);
    });
  }

  function initialiseSpecificationTables() {
    document.querySelectorAll('.eco-product-page .rte table').forEach((table) => {
      if (!isTwoColumnSpecificationTable(table)) return;

      table.classList.add('eco-spec-table--enhanced');
      table.dataset.ecoTable = 'specification';

      let thead = table.querySelector(':scope > thead');
      if (!thead) {
        thead = document.createElement('thead');
        const headerRow = document.createElement('tr');
        ['Specification', 'Detail'].forEach((label) => {
          const headerCell = document.createElement('th');
          headerCell.scope = 'col';
          headerCell.textContent = label;
          headerRow.appendChild(headerCell);
        });
        thead.appendChild(headerRow);
        table.insertBefore(thead, table.firstChild);
      } else {
        const headerCells = thead.querySelectorAll(':scope > tr:first-child > th, :scope > tr:first-child > td');
        if (headerCells.length === 2) {
          headerCells[0].textContent = 'Specification';
          headerCells[1].textContent = 'Detail';
        }
      }

      if (!table.parentElement?.classList.contains('eco-spec-table-wrapper')) {
        const wrapper = document.createElement('div');
        wrapper.className = 'eco-spec-table-wrapper';
        table.parentNode.insertBefore(wrapper, table);
        wrapper.appendChild(table);
      }
    });
  }

  function init() {
    initialiseCompatibilityPanels();
    initialiseCompatibilityToggles();
    initialiseDispatchPanels();
    initialiseSpecificationTables();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
